import React from 'react';
import { Typography } from '@ghq-abi/design-system-v2';

import { cn } from '~/lib/utils';

import { Spinner } from '../Spinner';

export interface LoadingStateProps {
  /**
   * Mensagem a ser exibida junto com o spinner
   * @default "Loading..."
   */
  message?: string;

  /**
   * Classes CSS adicionais para o container principal
   */
  className?: string;

  /**
   * Altura mínima do container em pixels
   * @default 200
   */
  minHeight?: number;

  /**
   * Padding vertical do container
   * @default "py-12"
   */
  verticalPadding?: string;

  /**
   * Variante do layout do loading
   * - "centered": Spinner e texto centralizados lado a lado (padrão)
   * - "stacked": Spinner acima do texto, ambos centralizados
   * - "drawer": Layout otimizado para drawers
   * - "card": Layout otimizado para cards
   */
  variant?: 'centered' | 'stacked' | 'drawer' | 'card';

  /**
   * Tamanho do spinner
   * - "sm": Pequeno
   * - "md": Médio (padrão)
   * - "lg": Grande
   */
  spinnerSize?: 'sm' | 'md' | 'lg';

  /**
   * Variante do texto Typography
   * @default "body-md-regular"
   */
  textVariant?:
    | 'body-sm-regular'
    | 'body-sm-medium'
    | 'body-md-regular'
    | 'body-md-medium';

  /**
   * Cor do texto Typography
   * @default "light"
   */
  textColor?: 'light' | 'default' | 'dark' | 'disabled';
}

const spinnerSizeClasses = {
  sm: 'h-4 w-4',
  md: 'h-8 w-8',
  lg: 'h-12 w-12'
};

export const LoadingState: React.FC<LoadingStateProps> = ({
  message = 'Loading...',
  className = '',
  minHeight = 200,
  verticalPadding,
  variant = 'centered',
  spinnerSize = 'md',
  textVariant = 'body-md-regular',
  textColor = 'light'
}) => {
  const getPadding = () => {
    if (verticalPadding) {
      return verticalPadding;
    }

    switch (variant) {
      case 'drawer':
        return 'py-6';
      case 'card':
        return 'py-8';
      case 'stacked':
        return 'py-12';
      default:
        return 'py-12';
    }
  };

  const renderContent = () => {
    const spinnerClass = cn(
      'animate-spin rounded-full border-b-2 border-blue-600',
      spinnerSizeClasses[spinnerSize]
    );

    switch (variant) {
      case 'stacked':
        return (
          <div className="text-center">
            <div className={cn(spinnerClass, 'mx-auto mb-4')}></div>
            {message && (
              <Typography variant={textVariant} color={textColor}>
                {message}
              </Typography>
            )}
          </div>
        );

      case 'drawer':
        return (
          <div className="text-center">
            <div className={cn(spinnerClass, 'mx-auto mb-4')}></div>
            {message && (
              <Typography variant={textVariant} color={textColor}>
                {message}
              </Typography>
            )}
          </div>
        );

      case 'card':
        return (
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <div className={spinnerClass}></div>
            </div>
            {message && (
              <Typography variant={textVariant} color={textColor}>
                {message}
              </Typography>
            )}
          </div>
        );

      default: // centered
        return (
          <div className="text-center">
            <div className="flex items-center justify-center gap-2">
              <Spinner />
              {message && (
                <Typography variant={textVariant} color={textColor}>
                  {message}
                </Typography>
              )}
            </div>
          </div>
        );
    }
  };

  return (
    <div
      className={cn(
        'flex items-center justify-center',
        getPadding(),
        className
      )}
      style={{ minHeight: `${minHeight}px` }}>
      {renderContent()}
    </div>
  );
};
